import { ttsApi } from '../api';
import {
  SpeechState,
  SpeechSynthesisConfig,
  SpeechStateChangeCallback,
  SpeechEventCallback,
} from './types';
import { TextSplitter } from './textSplitter';
import { AudioPlayer } from './audioPlayer';
import { ParallelTTSProcessor } from './parallelTTSProcessor';
import { QueueManager } from './queueManager';
import { DEFAULT_SPEECH_CONFIG } from './config';
import { ReplaceRule, SPEECH_REPLACE_RULES } from './speechReplaceRules';

/**
 * リファクタリング済み音声合成サービス
 * 単一責任の原則に従い、各機能を専用クラスに分離
 */
export class RefactoredSpeechSynthesisService {
  private config: SpeechSynthesisConfig;
  private textSplitter: TextSplitter;
  private audioPlayer: AudioPlayer;
  private parallelTTSProcessor: ParallelTTSProcessor;
  private queueManager: QueueManager;

  // stop() による中断中フラグ。中断時に processSplitSegments の finally で
  // notifySpeakingState(false) を二重に呼ばないようにする（speaking_ended の多重発火で、
  // スキップ後のユーザー発話の認識結果がクリアされる問題への対策）。
  private stopRequested = false;

  // コールバック
  private onSpeakingStateChange?: SpeechStateChangeCallback;
  private onEvent?: SpeechEventCallback;

  constructor(
    config: SpeechSynthesisConfig = DEFAULT_SPEECH_CONFIG,
    onSpeakingStateChange?: SpeechStateChangeCallback,
    onEvent?: SpeechEventCallback
  ) {
    this.config = config;
    this.onSpeakingStateChange = onSpeakingStateChange;
    this.onEvent = onEvent;

    // 各コンポーネントを初期化
    this.textSplitter = new TextSplitter(config.splitting);
    this.audioPlayer = new AudioPlayer();
    this.queueManager = new QueueManager(onEvent);
    this.parallelTTSProcessor = new ParallelTTSProcessor(
      this.audioPlayer,
      onEvent,
      config.delays.segmentTransition,
      config.speakingRate ?? 1.25
    );
  }

  /**
   * テキストを音声読み上げキューに追加
   */
  speak(text: string, enableSplitting: boolean = true): void {
    // 特定の単語を置換
    const replacedText = this.replaceText(text, SPEECH_REPLACE_RULES);

    // 長文分割処理
    if (
      enableSplitting &&
      this.config.enableSplitting &&
      this.textSplitter.shouldSplit(replacedText)
    ) {
      this.handleSplitText(replacedText);
      return;
    }

    // 通常のテキストをキューに追加
    this.queueManager.enqueueText(replacedText);
    this.processQueueIfIdle();
  }

  /**
   * 特定の文字列を置換
   * @param text 対象のテキスト
   * @param ReplaceRule[] 置換ルール
   * @returns 置換されたテキスト
   */
  private replaceText(text: string, replaceRules: ReplaceRule[]): string {
    try {
      return replaceRules.reduce(
        (result, rule) => result.replaceAll(rule.beforeReplace, rule.afterReplace),
        text
      );
    } catch (error) {
      console.error('❌ テキスト置換エラー:', error);
      // エラー時は元のテキストを返す
      return text;
    }
  }

  /**
   * 分割テキストを処理
   */
  private handleSplitText(text: string): void {
    const segments = this.textSplitter.split(text);

    if (segments.length <= 1) {
      // 分割できない場合は通常処理
      this.queueManager.enqueueText(text);
    } else {
      // 分割されたセグメントをキューに追加
      this.queueManager.enqueueSplitSegments(segments);
    }

    this.processQueueIfIdle();
  }

  /**
   * キューが停止中なら処理を開始
   */
  private processQueueIfIdle(): void {
    if (!this.queueManager.processing && !this.parallelTTSProcessor.isActive) {
      this.processQueue();
    }
  }

  /**
   * キューを処理
   */
  private async processQueue(): Promise<void> {
    // 並列モード中はキュー処理をスキップ
    if (this.parallelTTSProcessor.isActive) {
      return;
    }

    if (this.queueManager.isEmpty) {
      this.queueManager.setProcessing(false);
      return;
    }

    this.queueManager.setProcessing(true);
    const item = this.queueManager.dequeue();

    if (!item) {
      this.queueManager.setProcessing(false);
      return;
    }

    try {
      if (item.type === 'split_segments' && item.segments) {
        // 分割TTS処理
        await this.processSplitSegments(item.segments);
      } else {
        // 通常のTTS処理
        await this.processTextItem(item.content);
      }
    } catch (error) {
      console.error('❌ キューアイテム処理エラー:', error);
      this.onEvent?.({
        type: 'error',
        error: error instanceof Error ? error.message : 'キュー処理エラー',
      });
    }

    // 次のアイテムを処理
    setTimeout(() => {
      this.processQueue();
    }, this.config.delays.queueProcessing);
  }

  /**
   * 分割セグメントを処理
   */
  private async processSplitSegments(segments: string[]): Promise<void> {
    // 並列TTS(分割セグメント)でも、通常TTS(processTextItem)と同様に再生の開始/終了で
    // isSpeaking を通知する。これが無いと並列再生の完了後に onSpeakingStateChange(false)
    // が発火せず、音声認識が再開されない（AIが読み切った直後の発話が拾われない原因だった）。
    this.stopRequested = false;
    this.notifySpeakingState(true);
    try {
      await this.parallelTTSProcessor.process(segments);
    } finally {
      // stop() による中断時は stop() 側で notifySpeakingState(false) 済み。
      // ここで二重に呼ぶと speaking_ended が多重発火し、スキップ後にユーザーが
      // 喋った認識結果(currentTranscript)がクリアされてしまうため抑制する。
      if (!this.stopRequested) {
        this.notifySpeakingState(false);
      }
    }
  }

  /**
   * 通常のテキストアイテムを処理
   */
  private async processTextItem(text: string): Promise<void> {
    // 並列モードが開始された場合はキューに戻して待機
    if (this.parallelTTSProcessor.isActive) {
      this.queueManager.unshift({ type: 'text', content: text });
      this.queueManager.setProcessing(false);
      return;
    }

    try {
      // TTS生成
      const audioBlob = await ttsApi.synthesizeSpeech(text, undefined, this.config.speakingRate);

      // 並列モードチェック（再度）
      if (this.parallelTTSProcessor.isActive) {
        this.queueManager.unshift({ type: 'text', content: text });
        this.queueManager.setProcessing(false);
        return;
      }

      // 音声再生
      this.notifySpeakingState(true);
      await this.audioPlayer.play(audioBlob);
      this.notifySpeakingState(false);
    } catch (error) {
      console.error('🔴 TTS処理エラー:', error);
      console.error('🔴 処理に失敗したテキスト:', text);
      this.notifySpeakingState(false);

      // エラー時の遅延
      await new Promise((resolve) => setTimeout(resolve, this.config.delays.errorRecovery));
    }
  }

  /**
   * 現在の音声再生を停止
   */
  stop(): void {
    this.stopRequested = true;
    this.audioPlayer.stop();
    this.parallelTTSProcessor.stop();
    this.queueManager.clear();
    this.queueManager.setProcessing(false);
    this.notifySpeakingState(false);
  }

  /**
   * キューをクリア（再生中の音声は継続）
   */
  clearQueue(): void {
    this.queueManager.clear();
  }

  /**
   * 現在の状態を取得
   */
  getState(): SpeechState {
    return {
      isProcessing: this.queueManager.processing,
      isSpeaking: this.isSpeaking,
      queueLength: this.queueManager.length,
      currentMode: this.parallelTTSProcessor.isActive ? 'parallel' : 'normal',
    };
  }

  /**
   * 読み上げ中かどうか
   */
  get isSpeaking(): boolean {
    return this.audioPlayer.isPlaying || this.parallelTTSProcessor.isActive;
  }

  /**
   * キューの残り件数
   */
  get queueLength(): number {
    return this.queueManager.length;
  }

  /**
   * 設定を更新
   */
  updateConfig(config: Partial<SpeechSynthesisConfig>): void {
    this.config = { ...this.config, ...config };
    this.textSplitter.updateConfig(config.splitting || this.config.splitting);
    if (config.speakingRate !== undefined) {
      this.parallelTTSProcessor.setSpeakingRate(config.speakingRate);
      // 再生中の音声にも即座に反映
      this.audioPlayer.setPlaybackRate(config.speakingRate);
    }
  }

  /**
   * 読み上げ状態の変更を通知
   */
  private notifySpeakingState(isSpeaking: boolean): void {
    this.onSpeakingStateChange?.(isSpeaking);
    this.onEvent?.(isSpeaking ? { type: 'speaking_started' } : { type: 'speaking_ended' });
  }

  /**
   * 現在の状態をログ出力
   */
  private logCurrentState(): void {
    // ログ出力（デバッグ用 console.log は削除済み）
  }

  /**
   * サービスを破棄
   */
  dispose(): void {
    this.stop();
    this.audioPlayer.dispose();
    this.onSpeakingStateChange = undefined;
    this.onEvent = undefined;
  }
}
